import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  faCloud,
  faCloudBolt,
  faCloudRain,
  faCloudShowersHeavy,
  faCloudSun,
  faSmog,
  faSnowflake,
  faSunBright,
} from '@fortawesome/pro-regular-svg-icons';
import { AgentData, AgentStateService } from '@core/services/agent-state.service';
import { AuthenticationService } from '@core/services/auth.service';
import { ChatStateService } from '@core/services/chat-state.service';
import { CompanyService } from '@core/services/company.service';
import { CollapsibleMenuStateService } from '@core/services/collapsible-menu-state.service';
import {
  ConnectionStatus,
  ConnectionsStateService,
} from '@core/services/connections-state.service';
import { NavbarViewManagerService } from '@core/services/navbar-view-manager.service';
import { NavigationShellStateService } from '@core/services/navigation-shell-state.service';
import {
  LoginLog,
  LoginLogsResponse,
  LoginStatsResponse,
  UserLoginStatsService,
} from '@core/services/user-login-stats.service';
import { ProfileService } from '@core/services/profile.service';
import { SidebarService } from '@core/services/side-bar.service';
import { ChatService } from '@core/services/chat.service';
import { ChatConnectionsInitUtils } from '@core/utils/chat-connections-init.utils';
import { ConnectionsDisplayUtils } from '@core/utils/connections-display.utils';
import { AgentsCatalogService } from '../../agents/agents/agents-catalog.service';
import {
  HOME_AGENT_CARD_STYLES,
  HOME_ALL_CONNECTIONS,
  HOME_COMPANY_PROFILE_SECTIONS,
  HOME_CONNECTION_LOGOS,
  HOME_DEFAULT_EXPERT_CARDS,
  HOME_EXPERT_TO_AGENT_NAME_MAP,
  HOME_FIELD_ALIASES,
  HOME_LOADER_MIN_MS,
  HOME_MOTIVATIONAL_PHRASES,
  HOME_QUICK_ACTION_PROMPTS,
  HOME_ROLE_USUARIO_ID,
} from './home.constants';
import {
  ExpertCard,
  HomeConnectedConnectionViewModel,
  HomeExpertCardViewModel,
  HomeQuickActionPrompt,
  HomeRecentChatViewModel,
} from './home.models';

@Injectable()
export class HomeFacade {
  private subscriptions: Subscription[] = [];
  private promptIntervalId: any;

  userName = '';
  expertCards: ExpertCard[] = [];
  isLoadingExpertCards = false;
  chatMessage = '';
  isPromptTransitioning = false;
  currentPromptIndex = 0;
  motivationalPhrase = '';

  companyProfileProgress = 0;
  documentCount = 0;
  firstMissingField = '';

  weatherCity = '';
  weatherTemp = '';
  weatherDescription = '';
  weatherIcon: any = faSunBright;
  weatherIconColor = '#F59E0B';

  homeConnections: ConnectionStatus[] = [];

  recentChats: any[] = [];
  totalChatsCount = 0;
  isLoadingChats = false;

  loginStats: LoginStatsResponse | null = null;
  loginLogs: LoginLog[] = [];
  loginLogsTotal = 0;
  loginLogsPage = 1;
  loginLogsLastPage = 1;
  isAdmin = false;
  userRoleId: number | null = null;

  constructor(
    private agentStateService: AgentStateService,
    private authService: AuthenticationService,
    private chatStateService: ChatStateService,
    private companyService: CompanyService,
    private collapsibleMenuStateService: CollapsibleMenuStateService,
    private connectionsStateService: ConnectionsStateService,
    private navbarViewManagerService: NavbarViewManagerService,
    private navigationShellStateService: NavigationShellStateService,
    private userLoginStatsService: UserLoginStatsService,
    private profileService: ProfileService,
    private chatService: ChatService,
    private agentsCatalogService: AgentsCatalogService,
    private router: Router,
    private sidebarService: SidebarService,
  ) {}

  init(): void {
    this.subscriptions.push(
      this.authService.currentUser$.subscribe((user) => {
        if (!user) {
          return;
        }

        this.userName = user.name;

        if (!user.roles || !Array.isArray(user.roles)) {
          return;
        }

        const foundRole = user.roles.find((r: any) => r.id === 1 || r.id === 2);
        this.isAdmin = !!foundRole;
        this.userRoleId = foundRole ? foundRole.id : null;

        if (this.isAdmin) {
          this.loadLoginStats();
          this.loadLoginLogs();
        }

        const isRoleUsuario = user.roles.some(
          (r: any) => r.id === HOME_ROLE_USUARIO_ID,
        );

        if (isRoleUsuario) {
          this.loadUserAgentsForHome();
          this.connectionsStateService.reloadConnections();
          return;
        }

        this.expertCards = HOME_DEFAULT_EXPERT_CARDS.map((card) => ({ ...card }));
        this.isLoadingExpertCards = false;
        this.updateExpertConnections();
      }),
    );

    this.subscriptions.push(
      this.agentStateService.agents$.subscribe(() => {
        this.updateExpertConnections();
      }),
    );

    this.subscriptions.push(
      this.chatStateService.refreshChats$.subscribe(() => {
        this.loadRecentChats();
      }),
    );

    this.loadCompanyHomeData();
    this.loadHomeConnections();
    this.loadRecentChats();

    this.motivationalPhrase =
      HOME_MOTIVATIONAL_PHRASES[
        Math.floor(Math.random() * HOME_MOTIVATIONAL_PHRASES.length)
      ];

    this.startPromptRotation();
  }

  destroy(): void {
    if (this.promptIntervalId) {
      clearInterval(this.promptIntervalId);
    }

    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  loadLoginStats(): void {
    this.userLoginStatsService.getLoginStats(30).subscribe((stats) => {
      this.loginStats = stats;
    });
  }

  loadLoginLogs(page: number = 1): void {
    this.userLoginStatsService
      .getLoginLogs(10, page)
      .subscribe((logs: LoginLogsResponse) => {
        this.loginLogs = logs.data;
        this.loginLogsTotal = logs.total;
        this.loginLogsPage = logs.current_page;
        this.loginLogsLastPage = logs.last_page;
      });
  }

  onLoginLogsPageChange(page: number): void {
    this.loadLoginLogs(page);
  }

  startPromptRotation(): void {
    this.promptIntervalId = setInterval(() => {
      const prompts = this.visibleQuickActionPrompts;
      if (prompts.length === 0) {
        return;
      }

      this.isPromptTransitioning = true;

      setTimeout(() => {
        this.currentPromptIndex = (this.currentPromptIndex + 1) % prompts.length;
        setTimeout(() => {
          this.isPromptTransitioning = false;
        }, 100);
      }, 500);
    }, 5000);
  }

  get visibleQuickActionPrompts(): HomeQuickActionPrompt[] {
    const activeTitles = new Set(
      this.expertCards
        .filter((card) => !this.isExpertDisabled(card))
        .map((card) => card.title),
    );

    return HOME_QUICK_ACTION_PROMPTS.filter((prompt) =>
      activeTitles.has(prompt.expert),
    );
  }

  getCurrentPrompt(): HomeQuickActionPrompt {
    const prompts = this.visibleQuickActionPrompts;
    if (prompts.length === 0) {
      return { prompt: '', expert: '' };
    }

    return prompts[this.currentPromptIndex % prompts.length];
  }

  getNextPrompt(): HomeQuickActionPrompt {
    const prompts = this.visibleQuickActionPrompts;
    if (prompts.length === 0) {
      return { prompt: '', expert: '' };
    }

    return prompts[(this.currentPromptIndex + 1) % prompts.length];
  }

  goToCompleteCompany(): void {
    const queryParams = this.firstMissingField
      ? { field: this.firstMissingField }
      : {};
    this.router.navigate(['/dashboard/settings/company'], { queryParams });
  }

  loadCompanyHomeData(): void {
    this.profileService.getCompanyProfile().subscribe({
      next: (res) => {
        const company = res.company as any;
        this.companyProfileProgress = this.calculateProfileProgress(company);
        this.firstMissingField = this.findFirstMissingField(company);

        const city = company.city || company.province || '';
        if (city) {
          this.loadWeather(city);
        }
      },
      error: () => {
        this.companyProfileProgress = 0;
      },
    });

    this.profileService.getKnowledgeBaseDocuments({ per_page: 1 }).subscribe({
      next: (res) => {
        this.documentCount = res.total ?? 0;
      },
      error: () => {
        this.documentCount = 0;
      },
    });
  }

  loadHomeConnections(): void {
    this.subscriptions.push(
      this.connectionsStateService.connections$.subscribe((connections) => {
        this.homeConnections = connections.map((connection) => {
          const def = HOME_ALL_CONNECTIONS.find((item) => item.id === connection.id);
          return { ...connection, name: def?.name || connection.name };
        });
      }),
    );
  }

  getConnectionLogo(id: string): string {
    return HOME_CONNECTION_LOGOS[id] || '';
  }

  get connectedConnectionsList(): ConnectionStatus[] {
    return this.homeConnections.filter((connection) => connection.isConnected);
  }

  get totalConnectionsCount(): number {
    return HOME_ALL_CONNECTIONS.length;
  }

  get connectedCount(): number {
    return this.homeConnections.filter((connection) => connection.isConnected)
      .length;
  }

  get disconnectedCount(): number {
    const available = HOME_ALL_CONNECTIONS.filter(
      (connection) => connection.status === 'available',
    );
    return available.length - this.connectedCount;
  }

  get comingSoonCount(): number {
    return HOME_ALL_CONNECTIONS.filter(
      (connection) => connection.status === 'coming_soon',
    ).length;
  }

  get connectedConnectionsView(): HomeConnectedConnectionViewModel[] {
    return this.connectedConnectionsList.map((connection) => ({
      id: connection.id,
      name: connection.name,
      logoUrl: this.getConnectionLogo(connection.id),
      initials: this.getConnectionInitials(connection.name),
    }));
  }

  goToConnection(connectionId: string): void {
    if (['outlook', 'calendar', 'sharepoint'].includes(connectionId)) {
      this.router.navigate(['/dashboard/connections']);
      return;
    }

    this.router.navigate(['/dashboard/settings', 'connections'], {
      queryParams: { type: connectionId, action: 'edit' },
    });
  }

  loadRecentChats(): void {
    const startedAt = Date.now();
    this.isLoadingChats = true;

    this.chatService.getChats(1).subscribe({
      next: (res: any) => {
        const allChats = res.data || [];
        this.recentChats = allChats.slice(0, 3);
        this.totalChatsCount = res.total ?? allChats.length;
        this.finishLoading(startedAt, (value) => (this.isLoadingChats = value));
      },
      error: () => {
        this.finishLoading(startedAt, (value) => (this.isLoadingChats = value));
      },
    });
  }

  getChatQuestion(chat: any): string {
    const content =
      chat.first_user_line?.display_content ||
      chat.first_user_line?.content ||
      chat.title ||
      '';
    return content.length > 80 ? `${content.substring(0, 80)}...` : content;
  }

  getChatAgentName(chat: any): string {
    return chat.agent?.agent_config?.name || chat.agent?.name || 'Agente';
  }

  getChatAgentShortName(chat: any): string {
    const name = this.getChatAgentName(chat);
    for (const style of HOME_AGENT_CARD_STYLES) {
      if (typeof style.pattern !== 'string' && style.pattern.test(name)) {
        return style.shortTitle;
      }
    }

    return name;
  }

  getChatAgentBadgeClasses(chat: any): string {
    const name = (this.getChatAgentName(chat) || '').toLowerCase();
    if (/dirección|direccion|ceo/.test(name)) {
      return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
    }
    if (/financiero|fiscal/.test(name)) {
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    }
    if (/ventas|comercial/.test(name)) {
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    }
    if (/rrhh|operaciones/.test(name)) {
      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
    }
    if (/marketing|mercado/.test(name)) {
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    }
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }

  getChatDate(chat: any): string {
    const date = new Date(chat.created_at);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86400000);
    const chatDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const time = date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });

    if (chatDay.getTime() === today.getTime()) {
      return `Hoy ${time}`;
    }
    if (chatDay.getTime() === yesterday.getTime()) {
      return `Ayer ${time}`;
    }

    return (
      date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }) +
      ' ' +
      time
    );
  }

  get recentChatsView(): HomeRecentChatViewModel[] {
    return this.recentChats.map((chat) => ({
      key: chat.uuid || chat.id,
      raw: chat,
      question: this.getChatQuestion(chat),
      agentShortName: this.getChatAgentShortName(chat),
      agentBadgeClasses: this.getChatAgentBadgeClasses(chat),
      dateLabel: this.getChatDate(chat),
    }));
  }

  goToChat(chat: any): void {
    const chatId = chat.chat_id || chat.uuid || chat.id;
    if (!chatId) {
      return;
    }

    localStorage.setItem('chat_id', chatId);
    this.chatStateService.setChatId(chatId, true);
    this.router.navigate(['/dashboard/chat']);
  }

  goToAllChats(): void {
    this.router.navigate(['/dashboard/chat']);
  }

  newChat(): void {
    const allAgents = this.agentStateService.currentAgents || [];

    let targetAgent = allAgents.find(
      (agent) =>
        (agent.agent_config?.name || '').toLowerCase().includes('dirección') ||
        (agent.agent_config?.name || '').toLowerCase().includes('direccion') ||
        (agent.agent_config?.name || '').toLowerCase().includes('ceo'),
    );

    if (!targetAgent) {
      const companyAgents = this.agentStateService.getCompanyAgents();
      if (companyAgents && companyAgents.length > 0) {
        targetAgent = companyAgents[0];
      } else if (allAgents.length > 0) {
        targetAgent = allAgents[0];
      }
    }

    if (targetAgent) {
      this.setAgentFromRawAgent(targetAgent);
    } else {
      localStorage.removeItem('agent_image');
    }

    this.chatStateService.startNewChat(
      this.agentStateService,
      this.navbarViewManagerService,
    );
    this.router.navigate(['/dashboard/chat']);
    this.closeMobileNavigationIfNeeded();
  }

  goToExpertChat(expert: ExpertCard): void {
    if (this.setAgentFromExpertCard(expert)) {
      this.chatStateService.startNewChat(
        this.agentStateService,
        this.navbarViewManagerService,
      );
      this.router.navigate(['/dashboard/chat']);
      this.closeMobileNavigationIfNeeded();
      return;
    }

    this.newChat();
  }

  sendPromptWithExpert(prompt: string, expertName: string): void {
    localStorage.setItem('pending_prompt', prompt);
    localStorage.setItem('force_execute_prompt', 'true');

    const expertCard = this.expertCards.find((card) => card.title === expertName);
    if (expertCard) {
      this.setAgentFromExpertCard(expertCard);
    }

    this.dispatchPromptToChat(prompt);
  }

  sendChatMessage(): void {
    const prompt = this.chatMessage?.trim();
    if (!prompt) {
      return;
    }

    const allAgents = this.agentStateService.currentAgents || [];

    let targetAgent = allAgents.find(
      (agent) =>
        (agent.agent_config?.name || '').toLowerCase().includes('dirección') ||
        (agent.agent_config?.name || '').toLowerCase().includes('direccion') ||
        (agent.agent_config?.name || '').toLowerCase().includes('ceo'),
    );

    if (!targetAgent) {
      targetAgent = allAgents.find(
        (agent) => agent.agent_config?.name === 'Mi agente',
      );
    }

    if (!targetAgent) {
      const companyAgents = this.agentStateService.getCompanyAgents();
      if (companyAgents && companyAgents.length > 0) {
        targetAgent = companyAgents[0];
      } else if (allAgents.length > 0) {
        targetAgent = allAgents[0];
      }
    }

    if (targetAgent) {
      this.setAgentFromRawAgent(targetAgent);
    }

    this.sendPrompt(prompt);
    this.chatMessage = '';
  }

  updateExpertConnections(): void {
    this.expertCards.forEach((expert) => {
      let foundAgent = this.agentStateService.currentAgents?.find(
        (agent) =>
          agent.agent_config?.name?.toLowerCase() === expert.title.toLowerCase(),
      );

      if (!foundAgent) {
        const agentNameToFind = HOME_EXPERT_TO_AGENT_NAME_MAP[expert.title];
        foundAgent = this.agentStateService.currentAgents?.find(
          (agent) =>
            agent.agent_config?.name?.toLowerCase() ===
            agentNameToFind?.toLowerCase(),
        );
      }

      if (!foundAgent) {
        foundAgent = this.agentStateService.currentAgents?.find(
          (agent) =>
            agent.agent_config?.name
              ?.toLowerCase()
              .includes(expert.title.toLowerCase()) ||
            expert.title
              .toLowerCase()
              .includes(agent.agent_config?.name?.toLowerCase() || ''),
        );
      }

      if (!foundAgent) {
        return;
      }

      const idToUse =
        typeof foundAgent.agent_id === 'number' && !isNaN(foundAgent.agent_id)
          ? foundAgent.agent_id
          : typeof foundAgent.agent_config?.id === 'number' &&
              !isNaN(foundAgent.agent_config.id)
            ? foundAgent.agent_config.id
            : foundAgent.id;

      if (idToUse) {
        expert.id = idToUse;
      }

      const connectionIds = ChatConnectionsInitUtils.getConnectionsForAgent(
        foundAgent,
      );
      expert.connections =
        ConnectionsDisplayUtils.getActiveConnections(connectionIds);
    });
  }

  loadUserAgentsForHome(): void {
    const startedAt = Date.now();
    this.isLoadingExpertCards = true;

    this.companyService.getMyAgents().subscribe({
      next: (agents: any[]) => {
        const list = Array.isArray(agents) ? agents : [];
        const catalogAgents = list.map((agent: any) => this.toCatalogAgent(agent));
        const catalogGroups = this.agentsCatalogService.categorizeProducts(catalogAgents);
        const visibleAgents = this.agentsCatalogService.getAgentsForTab(
          catalogAgents,
          catalogGroups,
          'user',
        );

        this.expertCards = visibleAgents.map((agent: AgentData) => {
          const agentName = agent.agent_config?.name || '';
          const style = this.getAgentCardStyle(agentName);
          const defaultPrompt =
            HOME_QUICK_ACTION_PROMPTS.find(
              (prompt) => prompt.expert === agentName,
            )?.prompt ?? '';

          return {
            id: agent.agent_id || agent.id,
            iconPath: style.iconPath,
            iconBg: style.iconBg,
            tags: [],
            title: agentName,
            shortTitle: style.shortTitle || agentName,
            description: agent.agent_config?.description || '',
            features: style.features,
            defaultPrompt: defaultPrompt || undefined,
          } as ExpertCard;
        });

        this.finishLoading(startedAt, (value) => (this.isLoadingExpertCards = value));
      },
      error: () => {
        this.finishLoading(startedAt, (value) => (this.isLoadingExpertCards = value));
      },
    });
  }

  private toCatalogAgent(agent: Record<string, any>): AgentData {
    return {
      id: agent['id'],
      agent_id: agent['id'],
      category: 'user',
      agent_config: {
        id: agent['id'],
        name: agent['name'] || '',
        description: agent['description'] || '',
        icon: agent['icon'] || '',
        context: agent['context'] || '',
        disable: agent['disable'] ?? false,
        agent_custom_configs: [],
      },
    };
  }

  get activeExpertCount(): number {
    return this.expertCards.filter((expert) => !this.isExpertDisabled(expert))
      .length;
  }

  get totalExpertCount(): number {
    return this.expertCards.length;
  }

  get expertCardsView(): HomeExpertCardViewModel[] {
    return this.expertCards.map((expert) => ({
      ...expert,
      disabled: this.isExpertDisabled(expert),
    }));
  }

  get currentQuickPrompt(): HomeQuickActionPrompt | null {
    const prompt = this.getCurrentPrompt();
    return prompt.prompt ? prompt : null;
  }

  get nextQuickPrompt(): HomeQuickActionPrompt | null {
    const prompt = this.getNextPrompt();
    return prompt.prompt ? prompt : null;
  }

  isExpertDisabled(expert: ExpertCard): boolean {
    const expertTitle = (expert?.title || '').toLowerCase();
    return !(expertTitle.includes('direcci') && expertTitle.includes('ceo'));
  }

  private getFieldValue(company: any, field: string): any {
    const value = company[field];
    if (value !== null && value !== undefined && value.toString().trim() !== '') {
      return value;
    }

    const aliases = HOME_FIELD_ALIASES[field];
    if (aliases) {
      for (const alias of aliases) {
        const aliasValue = company[alias];
        if (
          aliasValue !== null &&
          aliasValue !== undefined &&
          aliasValue.toString().trim() !== ''
        ) {
          return aliasValue;
        }
      }
    }

    return null;
  }

  private calculateProfileProgress(company: any): number {
    let total = 0;
    const annualSales = company.annual_sales || {};
    const competitors = Array.isArray(company.competitors)
      ? company.competitors
      : [];

    for (const section of HOME_COMPANY_PROFILE_SECTIONS) {
      const filled = section.fields.filter((field) => {
        if (field.startsWith('sales_')) {
          const year = field.replace('sales_', '');
          const fromObj = annualSales[year] ?? annualSales[Number(year)];
          const fromFlat = company[field];
          return !!(fromObj ?? fromFlat);
        }

        if (field.startsWith('competitor_')) {
          const parts = field.split('_');
          const idx = +parts[1] - 1;
          const key = parts[2];
          const competitor = competitors[idx];
          if (!competitor) {
            return false;
          }
          const value = key === 'nif' ? competitor.cnae || competitor.nif : competitor[key];
          return value && value.toString().trim() !== '';
        }

        return this.getFieldValue(company, field) !== null;
      }).length;

      total += (filled / section.fields.length) * section.weight;
    }

    return Math.round(total);
  }

  private findFirstMissingField(company: any): string {
    const annualSales = company.annual_sales || {};
    const competitors = Array.isArray(company.competitors)
      ? company.competitors
      : [];

    for (const section of HOME_COMPANY_PROFILE_SECTIONS) {
      for (const field of section.fields) {
        if (field.startsWith('sales_')) {
          const year = field.replace('sales_', '');
          const value =
            annualSales[year] ?? annualSales[Number(year)] ?? company[field];
          if (!value) {
            return field;
          }
          continue;
        }

        if (field.startsWith('competitor_')) {
          const parts = field.split('_');
          const idx = +parts[1] - 1;
          const key = parts[2];
          const competitor = competitors[idx];
          const value = competitor
            ? key === 'nif'
              ? competitor.cnae || competitor.nif
              : competitor[key]
            : null;

          if (!value || value.toString().trim() === '') {
            return field;
          }
          continue;
        }

        if (this.getFieldValue(company, field) === null) {
          return field;
        }
      }
    }

    return '';
  }

  private loadWeather(city: string): void {
    const url = `https://wttr.in/${encodeURIComponent(city)}?format=j1&lang=es`;

    fetch(url)
      .then((response) => response.json())
      .then((data: any) => {
        const current = data.current_condition?.[0];
        if (!current) {
          return;
        }

        this.weatherCity = city.toUpperCase();
        this.weatherTemp = `${current.temp_C}°C`;
        const desc =
          current.lang_es?.[0]?.value || current.weatherDesc?.[0]?.value || '';
        this.weatherDescription = desc.toUpperCase();
        this.setWeatherStyle(current.weatherCode);
      })
      .catch(() => {});
  }

  private setWeatherStyle(code: string): void {
    const currentCode = parseInt(code, 10);
    if (currentCode === 113) {
      this.weatherIcon = faSunBright;
      this.weatherIconColor = '#F59E0B';
      return;
    }
    if (currentCode === 116) {
      this.weatherIcon = faCloudSun;
      this.weatherIconColor = '#F59E0B';
      return;
    }
    if (currentCode === 119 || currentCode === 122) {
      this.weatherIcon = faCloud;
      this.weatherIconColor = '#9CA3AF';
      return;
    }
    if ([176, 263, 266, 293, 296].includes(currentCode)) {
      this.weatherIcon = faCloudRain;
      this.weatherIconColor = '#3B82F6';
      return;
    }
    if ([299, 302, 305, 308, 353, 356, 359].includes(currentCode)) {
      this.weatherIcon = faCloudShowersHeavy;
      this.weatherIconColor = '#1D4ED8';
      return;
    }
    if (
      [179, 227, 230, 323, 326, 329, 332, 335, 338, 368, 371, 374, 377, 395].includes(
        currentCode,
      )
    ) {
      this.weatherIcon = faSnowflake;
      this.weatherIconColor = '#93C5FD';
      return;
    }
    if ([200, 386, 389, 392].includes(currentCode)) {
      this.weatherIcon = faCloudBolt;
      this.weatherIconColor = '#FBBF24';
      return;
    }
    if ([143, 248, 260].includes(currentCode)) {
      this.weatherIcon = faSmog;
      this.weatherIconColor = '#D1D5DB';
      return;
    }

    this.weatherIcon = faCloudSun;
    this.weatherIconColor = '#F59E0B';
  }

  private getAgentCardStyle(agentName: string): {
    iconPath: string;
    iconBg: string;
    shortTitle: string;
    features: string[];
  } {
    const name = (agentName || '').toLowerCase();
    for (const style of HOME_AGENT_CARD_STYLES) {
      if (
        typeof style.pattern === 'string'
          ? name.includes(style.pattern.toLowerCase())
          : style.pattern.test(name)
      ) {
        return {
          iconPath: style.iconPath,
          iconBg: style.iconBg,
          shortTitle: style.shortTitle,
          features: style.features,
        };
      }
    }

    return {
      iconPath: 'assets/images/support.svg',
      iconBg: '#E0E7FF',
      shortTitle: '',
      features: [],
    };
  }

  private finishLoading(
    startedAt: number,
    setLoading: (value: boolean) => void,
  ): void {
    const elapsed = Date.now() - startedAt;
    const remaining = HOME_LOADER_MIN_MS - elapsed;
    if (remaining > 0) {
      setTimeout(() => setLoading(false), remaining);
      return;
    }

    setLoading(false);
  }

  private getConnectionInitials(name: string): string {
    return (name || '').trim().substring(0, 2).toUpperCase();
  }

  private sendPrompt(prompt: string): void {
    localStorage.setItem('pending_prompt', prompt);
    localStorage.setItem('force_execute_prompt', 'true');
    this.dispatchPromptToChat(prompt);
  }

  private dispatchPromptToChat(prompt: string): void {
    this.chatStateService.startNewChat(
      this.agentStateService,
      this.navbarViewManagerService,
    );

    if (this.router.url === '/dashboard/chat') {
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('sendPromptToChat', { detail: { prompt } }),
        );
      }, 100);
    } else {
      this.router.navigate(['/dashboard/chat']).then(() => {
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('sendPromptToChat', { detail: { prompt } }),
          );
        }, 300);
      });
    }

    this.closeMobileNavigationIfNeeded();
  }

  private setAgentFromExpertCard(expert: ExpertCard): boolean {
    if (expert.id) {
      localStorage.setItem('type_agent', 'agent');
      localStorage.setItem('agent_id', expert.id.toString());
      localStorage.removeItem('agent_image');
      this.agentStateService.setAgentId(expert.id);
      this.agentStateService.setCurrentAgent(expert.title);
      this.chatStateService.setCurrentAgent(expert.title);
      return true;
    }

    const allAgents = this.agentStateService.currentAgents || [];
    const agentNameToFind = HOME_EXPERT_TO_AGENT_NAME_MAP[expert.title];
    let foundAgent = allAgents.find(
      (agent) =>
        agent.agent_config?.name?.toLowerCase() ===
        agentNameToFind?.toLowerCase(),
    );

    if (!foundAgent) {
      foundAgent = allAgents.find(
        (agent) =>
          agent.agent_config?.name
            ?.toLowerCase()
            .includes(expert.title.toLowerCase()) ||
          expert.title
            .toLowerCase()
            .includes(agent.agent_config?.name?.toLowerCase() || ''),
      );
    }

    return foundAgent ? this.setAgentFromRawAgent(foundAgent, expert.title) : false;
  }

  private setAgentFromRawAgent(agent: any, fallbackName?: string): boolean {
    const idToUse =
      typeof agent.agent_id === 'number' && !isNaN(agent.agent_id)
        ? agent.agent_id
        : typeof agent.agent_config?.id === 'number' &&
            !isNaN(agent.agent_config.id)
          ? agent.agent_config.id
          : agent.id;

    if (!idToUse) {
      return false;
    }

    const agentType = agent.category === 'datasources' ? 'datasource' : 'agent';
    const agentName = agent.agent_config?.name || fallbackName || '';

    localStorage.setItem('type_agent', agentType);
    localStorage.setItem('agent_id', idToUse.toString());
    localStorage.removeItem('agent_image');
    this.agentStateService.setAgentId(idToUse);
    this.agentStateService.setCurrentAgent(agentName);
    this.chatStateService.setCurrentAgent(agentName);
    return true;
  }

  private closeMobileNavigationIfNeeded(): void {
    this.navigationShellStateService.state$
      .pipe(take(1))
      .subscribe((state) => {
        if (!state.isMobile) {
          return;
        }

        if (state.isMenuExpanded) {
          this.collapsibleMenuStateService.setMenuExpanded(false);
        }

        if (state.isSidebarOpen) {
          this.sidebarService.toggleSidebar();
        }
      });
  }
}
